import { useCallback, useState } from 'react';
import { Team } from '@domain/eventTeam';

import { ChannelRow } from './channel-row';
import { RowElement } from './row-element';
import { useTeamChannelContext } from './team-channel-section-context';
import { isTeamSalesClosed } from './validators';

interface TeamSectionProps {
  team: Team;
  isMobileViewPort: boolean;
  canCloseSales: boolean;
  labelClassName?: string;
}

export const TeamSection = ({ team, isMobileViewPort, canCloseSales, labelClassName }: TeamSectionProps) => {
  const { getTeamError, getChannelError, updateTeamMax, updateChannelMax, setTeamSalesClosed, setChannelSalesClosed } =
    useTeamChannelContext();

  const teamSalesClosed = isTeamSalesClosed(team);

  // Default-open when a child is customised — its own cap differs from the team cap (so a capped team with
  // per-channel overrides still reveals them), or it's sales-closed. A fully-closed team stays collapsed.
  const defaultChannelsVisible =
    !teamSalesClosed &&
    team.channels.some(
      (c) => c.isTicketSalesClosed || (c.maxTicketQuantity !== null && c.maxTicketQuantity !== team.maxTicketQuantity)
    );

  const [isChannelsVisible, setIsChannelsVisible] = useState<boolean | undefined>(undefined);
  const openChannels = isChannelsVisible === undefined ? defaultChannelsVisible : isChannelsVisible;

  const toggleChevron = useCallback(() => {
    setIsChannelsVisible((prev) => (prev === undefined ? !defaultChannelsVisible : !prev));
  }, [defaultChannelsVisible]);

  const handleTeamMaxChange = useCallback(
    (next: number | null) => updateTeamMax(team.id, next),
    [team.id, updateTeamMax]
  );

  const handleTeamToggle = useCallback(
    (isClosed: boolean) => setTeamSalesClosed(team.id, isClosed),
    [team.id, setTeamSalesClosed]
  );

  const handleChannelSalesClosed = useCallback(
    (channelId: string, isClosed: boolean) => {
      setChannelSalesClosed(channelId, isClosed);
      // Same reason as handleTeamToggle: keep the channels visible when the
      // user closes one, so the affected row stays on screen.
      if (isClosed) setIsChannelsVisible(true);
    },
    [setChannelSalesClosed]
  );

  const errorStylesTeam = isMobileViewPort ? 'ml-11' : 'ml-9';

  return (
    <>
      <RowElement
        rowId={`team-${team.id}`}
        name={team.name}
        isMobileViewPort={isMobileViewPort}
        isSalesClosed={teamSalesClosed}
        maxTicketQuantity={team.maxTicketQuantity}
        soldItems={team.soldItems.tickets}
        showChevron={true}
        isChannelsVisible={openChannels}
        errorStyles={errorStylesTeam}
        error={getTeamError(team.id)}
        onMaxQuantityChange={handleTeamMaxChange}
        onToggleSalesClosed={handleTeamToggle}
        canCloseSales={canCloseSales}
        labelClassName={labelClassName}
        onToggleChevron={toggleChevron}
      />
      {openChannels && (
        <div className='flex flex-col gap-4'>
          {team.channels.map((channel) => (
            <ChannelRow
              key={channel.channel.id}
              channel={channel}
              isMobileViewPort={isMobileViewPort}
              error={getChannelError(channel.channel.id)}
              isNested
              updateChannelMax={updateChannelMax}
              setChannelSalesClosed={handleChannelSalesClosed}
              canCloseSales={canCloseSales}
              labelClassName={labelClassName}
            />
          ))}
        </div>
      )}
    </>
  );
};
